import Layout from "@/components/Layout";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import PermissionDenied from "@/components/PermissionDenied";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  ATTENDANCE_RULE_TYPES,
  ATTENDANCE_RULE_TYPE_LABELS,
  attendanceApiErrorMessage,
  createAttendanceRule,
  deleteAttendanceRule,
  fetchAllAttendanceRules,
  updateAttendanceRule,
  type AttendanceRuleCategory,
  type AttendanceRuleRecord,
  type AttendanceRuleValue,
} from "@/lib/hr/attendanceStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { motion } from "framer-motion";
import {
  CalendarIcon,
  Code2,
  Edit,
  Filter,
  Hash,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Save,
  Search,
  Tag,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const TITLE_REGEX = /^[a-zA-Z\s]{1,150}$/;
const CODE_REGEX = /^[a-z][a-z0-9_]{0,79}$/;

type ValueKind = "string" | "integer" | "boolean";
type RuleSortBy =
  | "status_active_first"
  | "status_inactive_first"
  | "latest"
  | "oldest"
  | "title_asc"
  | "title_desc"
  | "code_asc";

/**
 * Preview of `AttendanceRule::uniqueCodeFromTitle` (base segment only).
 * Matches Laravel `Str::slug($title, '_')` + `Str::limit(72)` for typical Latin letter titles.
 */
function previewAttendanceRuleCodeFromTitle(title: string): string {
  const trimmed = title.trim().toLowerCase();
  const decomposed = trimmed.normalize("NFD");
  const withoutMarks = decomposed.replace(/\p{M}/gu, "");
  const slug = withoutMarks
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  let base = slug.slice(0, 72);
  if (base === "") {
    base = "rule";
  }
  return base.slice(0, 80);
}

function formatRuleValue(v: AttendanceRuleValue): string {
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

function detectValueKind(v: AttendanceRuleValue): ValueKind {
  if (typeof v === "boolean") return "boolean";
  if (typeof v === "number") return "integer";
  return "string";
}

function buildValue(kind: ValueKind, stringVal: string, intVal: string, boolVal: boolean): AttendanceRuleValue | null {
  if (kind === "boolean") return boolVal;
  if (kind === "integer") {
    const n = Number.parseInt(intVal.replace(/[^\d-]/g, ""), 10);
    if (!Number.isFinite(n)) return null;
    return n;
  }
  return stringVal;
}

export default function AttendanceRules() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "attendance_rule_can_create");
  const canUpdate = can(user, "attendance_rule_can_update");
  const canDelete = can(user, "attendance_rule_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [allRows, setAllRows] = useState<AttendanceRuleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterType, setFilterType] = useState<string>("__all__");
  const [filterStatus, setFilterStatus] = useState<string>("__all__");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [sortBy, setSortBy] = useState<RuleSortBy>("status_active_first");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
  const [editingId, setEditingId] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [code, setCode] = useState("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [type, setType] = useState<AttendanceRuleCategory>("attendance");
  const [valueKind, setValueKind] = useState<ValueKind>("string");
  const [stringVal, setStringVal] = useState("");
  const [intVal, setIntVal] = useState("");
  const [boolVal, setBoolVal] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<AttendanceRuleRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const typeLabels = useMemo(() => ATTENDANCE_RULE_TYPE_LABELS, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const rows = await fetchAllAttendanceRules();
      setAllRows(rows);
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setAllRows([]);
        return;
      }
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (filterType !== "__all__" && r.type !== filterType) return false;
      if (filterStatus === "Active" && !r.isActive) return false;
      if (filterStatus === "Inactive" && r.isActive) return false;

      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(r.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }

      if (quickDate !== "all") {
        const created = parseISO(r.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today"
            ? startOfDay(now)
            : quickDate === "week"
              ? startOfDay(subDays(now, 6))
              : startOfMonth(now);
        if (created < start || created > end) return false;
      }

      if (!q) return true;
      return (
        r.title.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q) ||
        formatRuleValue(r.value).toLowerCase().includes(q) ||
        typeLabels[r.type].toLowerCase().includes(q)
      );
    });
  }, [allRows, query, filterType, filterStatus, filterCreatedDate, quickDate, typeLabels]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "status_active_first": {
          const sa = a.isActive ? 0 : 1;
          const sb = b.isActive ? 0 : 1;
          if (sa !== sb) return sa - sb;
          return a.title.localeCompare(b.title);
        }
        case "status_inactive_first": {
          const sa = a.isActive ? 1 : 0;
          const sb = b.isActive ? 1 : 0;
          if (sa !== sb) return sa - sb;
          return a.title.localeCompare(b.title);
        }
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "title_desc":
          return b.title.localeCompare(a.title);
        case "code_asc":
          return a.code.localeCompare(b.code);
        case "title_asc":
          return a.title.localeCompare(b.title);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterType !== "__all__" ? 1 : 0) +
    (filterStatus !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0);

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterType}|${filterStatus}|${filterCreatedDate?.toISOString() ?? ""}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, { defaultPageSize: 20 });

  const resetSheetFilters = () => {
    setFilterType("__all__");
    setFilterStatus("__all__");
    setFilterCreatedDate(undefined);
  };

  const resetForm = () => {
    setTitle("");
    setCode("");
    setCodeTouched(false);
    setDescription("");
    setType("attendance");
    setValueKind("string");
    setStringVal("");
    setIntVal("");
    setBoolVal(false);
    setIsActive(true);
    setEditingId(null);
  };

  const openCreate = () => {
    setDialogMode("create");
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (r: AttendanceRuleRecord) => {
    setDialogMode("edit");
    setEditingId(r.id);
    setTitle(r.title);
    setCode(r.code);
    setCodeTouched(true);
    setDescription(r.description);
    setType(r.type);
    const k = detectValueKind(r.value);
    setValueKind(k);
    if (k === "boolean") setBoolVal(Boolean(r.value));
    else if (k === "integer") setIntVal(String(r.value));
    else setStringVal(typeof r.value === "string" ? r.value : String(r.value));
    setIsActive(r.isActive);
    setDialogOpen(true);
  };

  const handleTitleChange = (nextTitle: string) => {
    setTitle(nextTitle);
    if (dialogMode === "create" && !codeTouched) {
      setCode(nextTitle.trim() === "" ? "" : previewAttendanceRuleCodeFromTitle(nextTitle));
    }
  };

  const handleCodeChange = (nextCode: string) => {
    setCodeTouched(true);
    setCode(
      nextCode
        .toLowerCase()
        .replace(/[^a-z0-9_]/g, "")
        .slice(0, 80),
    );
  };

  const handleSave = async () => {
    const t = title.trim();
    if (!TITLE_REGEX.test(t)) {
      toast.error("Title must be 1–150 characters, letters and spaces only.");
      return;
    }
    const c = code.trim().toLowerCase();
    if (!CODE_REGEX.test(c)) {
      toast.error("Code must start with a letter and use only lowercase letters, numbers, and underscores (max 80).");
      return;
    }
    const desc = description.trim();
    if (desc.length > 1000) {
      toast.error("Description is too long (max 1000 characters).");
      return;
    }
    const value = buildValue(valueKind, stringVal, intVal, boolVal);
    if (value === null && valueKind === "integer") {
      toast.error("Enter a valid integer for the value.");
      return;
    }
    if (valueKind === "string" && stringVal.length > 2000) {
      toast.error("String value is too long (max 2000 characters).");
      return;
    }
    const finalValue = value ?? stringVal;

    setSaving(true);
    try {
      if (dialogMode === "create") {
        const { message } = await createAttendanceRule({
          title: t,
          code: c,
          description: desc,
          type,
          value: finalValue,
          isActive,
        });
        toast.success(message);
      } else if (editingId) {
        const { message } = await updateAttendanceRule(editingId, {
          title: t,
          code: c,
          description: desc,
          type,
          value: finalValue,
          isActive,
        });
        toast.success(message);
      }
      setDialogOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { message } = await deleteAttendanceRule(deleteTarget.id);
      toast.success(message);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(attendanceApiErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const statusBadge = (r: AttendanceRuleRecord) =>
    r.isActive
      ? { label: "Active", cls: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400" }
      : { label: "Inactive", cls: "bg-muted text-muted-foreground" };

  const quickDateButtons = (
    <>
      {(
        [
          { id: "today" as const, label: "Today", mobile: "Today" },
          { id: "week" as const, label: "Week", mobile: "7D" },
          { id: "month" as const, label: "Month", mobile: "30D" },
          { id: "all" as const, label: "All", mobile: "All" },
        ] as const
      ).map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => setQuickDate(opt.id)}
          className={cn(
            "h-full px-3 text-sm transition-colors",
            quickDate === opt.id
              ? "bg-primary/10 text-foreground font-medium"
              : "text-muted-foreground hover:bg-muted/50",
          )}
        >
          <span className="sm:hidden">{opt.mobile}</span>
          <span className="hidden sm:inline">{opt.label}</span>
        </button>
      ))}
    </>
  );

  const viewModeButtons = (
    <>
      <button
        type="button"
        onClick={() => setViewMode("grid")}
        className={cn(
          "h-full px-3 text-sm transition-colors",
          viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
        )}
        aria-label="Grid view"
      >
        <LayoutGrid className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => setViewMode("list")}
        className={cn(
          "h-full px-3 text-sm transition-colors",
          viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
        )}
        aria-label="List view"
      >
        <List className="h-4 w-4" />
      </button>
    </>
  );

  const sortSelect = (
    <Select value={sortBy} onValueChange={(v) => setSortBy(v as RuleSortBy)}>
      <SelectTrigger className="glass-input h-10 w-full">
        <SelectValue placeholder="Sort by" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="status_active_first">Status: Active first</SelectItem>
        <SelectItem value="status_inactive_first">Status: Inactive first</SelectItem>
        <SelectItem value="latest">Newest first</SelectItem>
        <SelectItem value="oldest">Oldest first</SelectItem>
        <SelectItem value="title_asc">Title: A to Z</SelectItem>
        <SelectItem value="title_desc">Title: Z to A</SelectItem>
        <SelectItem value="code_asc">Code</SelectItem>
      </SelectContent>
    </Select>
  );

  return isForbidden ? (
    <PermissionDenied
      title="Attendance rules access required"
      message="You don’t have permission to view attendance rules. Ask an administrator to grant attendance_rule_can_view_any."
    />
  ) : (
    <Layout title="Attendance rules">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-display font-bold text-foreground">Attendance rules</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Key–value rules with a category (attendance or leave).
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2 shrink-0" onClick={openCreate}>
              <Plus className="w-4 h-4" />
              <span className="sm:hidden">Add</span>
              <span className="hidden sm:inline">Add rule</span>
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, code, description, value…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter attendance rules</SheetTitle>
                  <SheetDescription>Refine by type, status, or created date.</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Type</Label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All types</SelectItem>
                        {ATTENDANCE_RULE_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {typeLabels[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Created date</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "glass-input h-10 w-full justify-start text-left font-normal",
                            !filterCreatedDate && "text-muted-foreground",
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                          {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                        <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                        <div className="flex gap-2 border-t p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="flex-1"
                            onClick={() => setFilterCreatedDate(undefined)}
                          >
                            Clear
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={resetSheetFilters}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {quickDateButtons}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              {viewModeButtons}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect id="attendance-rules-page-size" pageSize={pageSize} onPageSizeChange={setPageSize} />
            <div className="w-[170px] shrink-0">{sortSelect}</div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="attendance-rules-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">{sortSelect}</div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {quickDateButtons}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                {viewModeButtons}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading attendance rules…
          </div>
        ) : null}

        {!loading && filtered.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {allRows.length === 0
              ? canCreate
                ? "No rules yet. Click “Add rule” to create one."
                : "No rules yet."
              : "No rules match your search."}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((r, i) => {
              const badge = statusBadge(r);
              return (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="h-full"
                >
                  <CatalogEntityCard
                    className="h-full flex flex-col"
                    title={r.title}
                    titleIcon={Tag}
                    statusBadge={{ label: badge.label, className: badge.cls }}
                    primaryMeta={{
                      label: "Code",
                      value: r.code,
                      icon: Code2,
                      iconClassName: "text-emerald-600",
                    }}
                    secondaryMeta={{
                      label: "Value",
                      value: formatRuleValue(r.value),
                      icon: Hash,
                      iconClassName: "text-sky-600",
                    }}
                    footer={
                      <span className="text-xs text-muted-foreground">
                        {typeLabels[r.type]} · {formatCatalogAuditDate(r.createdAt)}
                      </span>
                    }
                    onEdit={canUpdate ? () => openEdit(r) : undefined}
                    onDelete={canDelete ? () => setDeleteTarget(r) : undefined}
                  />
                </motion.div>
              );
            })}
          </div>
        ) : null}

        {!loading && filtered.length > 0 && viewMode === "list" ? (
          <div className="glass-card overflow-hidden rounded-xl border border-border/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/20 text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Title</th>
                    <th className="px-4 py-3 font-medium">Description</th>
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Value</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    {canUpdate || canDelete ? (
                      <th className="px-4 py-3 font-medium w-[120px] text-right">Actions</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((r) => (
                    <tr key={r.id} className="border-b border-border/40 last:border-0 hover:bg-muted/10">
                      <td className="px-4 py-3 font-medium text-foreground whitespace-nowrap">{r.title}</td>
                      <td className="px-4 py-3 text-muted-foreground max-w-[280px]">
                        {r.description ? (
                          <span className="line-clamp-2 text-xs leading-relaxed" title={r.description}>
                            {r.description}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground/60">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <code className="rounded bg-muted px-2 py-0.5 text-xs font-mono">{r.code}</code>
                      </td>
                      <td
                        className="px-4 py-3 text-muted-foreground max-w-[240px] truncate"
                        title={formatRuleValue(r.value)}
                      >
                        {formatRuleValue(r.value)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
                            r.type === "attendance"
                              ? "border-primary/25 bg-primary/10 text-primary"
                              : "border-violet-500/25 bg-violet-500/10 text-violet-800 dark:text-violet-200",
                          )}
                        >
                          {typeLabels[r.type]}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex rounded-full border px-2 py-0.5 text-xs font-medium",
                            r.isActive
                              ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
                              : "border-border bg-muted/40 text-muted-foreground",
                          )}
                        >
                          {r.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      {canUpdate || canDelete ? (
                        <td className="px-4 py-3 text-right space-x-1">
                          {canUpdate ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEdit(r)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canDelete ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => setDeleteTarget(r)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {!loading && filtered.length > 0 ? (
          <GridPaginationFooter
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            totalCount={totalCount}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogMode === "create" ? "Add attendance rule" : "Edit attendance rule"}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="rule-title">Title</Label>
              <Input
                id="rule-title"
                className="glass-input"
                maxLength={150}
                value={title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="e.g. Grace window minutes"
              />
              <p className="text-[11px] text-muted-foreground">Letters and spaces only, max 150 characters.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rule-code">Code</Label>
              <Input
                id="rule-code"
                className="glass-input font-mono text-xs"
                maxLength={80}
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="e.g. coming_late_grace_minutes"
              />
              <p className="text-[11px] text-muted-foreground">
                Lowercase letters, numbers, and underscores. Must be unique.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label>Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as AttendanceRuleCategory)}>
                <SelectTrigger className="glass-input h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ATTENDANCE_RULE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {typeLabels[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Value type</Label>
              <Select value={valueKind} onValueChange={(v) => setValueKind(v as ValueKind)}>
                <SelectTrigger className="glass-input h-10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="integer">Integer</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label>Value</Label>
              {valueKind === "string" ? (
                <Input
                  className="glass-input"
                  value={stringVal}
                  onChange={(e) => setStringVal(e.target.value)}
                  placeholder="Text"
                />
              ) : null}
              {valueKind === "integer" ? (
                <Input
                  className="glass-input"
                  inputMode="numeric"
                  value={intVal}
                  onChange={(e) => setIntVal(e.target.value.replace(/[^\d-]/g, ""))}
                  placeholder="Whole number"
                />
              ) : null}
              {valueKind === "boolean" ? (
                <div className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2">
                  <span className="text-sm text-muted-foreground">Enabled</span>
                  <Switch checked={boolVal} onCheckedChange={setBoolVal} />
                </div>
              ) : null}
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <div className="flex items-center justify-between rounded-lg border border-border/50 px-3 py-2">
                <div>
                  <Label htmlFor="rule-active">Active</Label>
                  <p className="text-[11px] text-muted-foreground">
                    Inactive rules are hidden from work and leave policy catalogs.
                  </p>
                </div>
                <Switch id="rule-active" checked={isActive} onCheckedChange={setIsActive} />
              </div>
            </div>

            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="rule-description">Description</Label>
              <Textarea
                id="rule-description"
                className="glass-input min-h-[80px] resize-y"
                maxLength={1000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short explanation of what this rule does…"
              />
              <p className="text-[11px] text-muted-foreground">
                Optional. Shown on the rules list and when configuring work policies.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="glass" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="premium"
              className="gap-2"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmAlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && !deleting && setDeleteTarget(null)}
        title="Delete this rule?"
        description={
          deleteTarget ? (
            <>
              Remove <strong>{deleteTarget.title}</strong> (<code className="text-xs">{deleteTarget.code}</code>)?
            </>
          ) : (
            ""
          )
        }
        mode="confirm"
        variant="danger"
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        onConfirm={() => void confirmDelete()}
      />
    </Layout>
  );
}
